import { CustomerConfigRequest } from "../models/Requests/CustomerConfigRequest";
import {
  CustomerConfigResponse,
  GetCustomerConfigResponse,
} from "../models/Responses/CustomerConfigResponse";
import { CreateParRequest } from "../models/Requests/PARSettingsRequest";
import { IPARValuesResponse } from "../models/Responses/IPARValuesResponse";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { getSessionStorageItem } from "../utils/utils";
import { apiClient } from "./ApiClient";

export class CustomerConfigService {
  /**
   * Save customer configuration to ForecastIQ API
   */
  static async saveCustomerConfig(
    config: CustomerConfigRequest,
  ): Promise<CustomerConfigResponse> {
    try {
      const response = await apiClient.post<CustomerConfigResponse>(
        "/customers/create_or_update",
        config,
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Error saving customer config:", error);
      // Preserve Axios error shape so callers can read response.data.message
      throw error;
    }
  }

  /**
   * Get customer configuration from ForecastIQ API
   */
  static async getCustomerConfig(): Promise<GetCustomerConfigResponse> {
    try {
      const response = await apiClient.get<GetCustomerConfigResponse>(
        "/customers/get_customer_config",
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Error fetching customer config:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch customer configuration";
      throw new Error(errorMessage);
    }
  }

  /**
   * Get measuring units for PAR settings
   */
  static async getMeasuringUnits(): Promise<unknown> {
    try {
      const response = await apiClient.get("/customers/measuring-units", {
        headers: {
          "custom-authorization":
            getSessionStorageItem(STORAGE_KEYS.UserContextToken) || "",
        },
      });
      return response.data;
    } catch (error: unknown) {
      console.error("Error fetching measuring units:", error);
      throw error;
    }
  }

  /**
   * Get PAR values from ForecastIQ API
   */
  static async getPARValues(
    corpEntityId: number,
    practiceId: number,
    siteId?: number,
    pageNumber = 1,
    pageSize = 10,
  ): Promise<IPARValuesResponse> {
    try {
      const queryParams = new URLSearchParams({
        corp_entity_id: String(corpEntityId),
        practice_id: String(practiceId),
        page_number: String(pageNumber),
        page_size: String(pageSize),
      });
      if (typeof siteId === "number" && siteId > 0) {
        queryParams.append("site_id", String(siteId));
      }

      const response = await apiClient.get<IPARValuesResponse>(
        `/customers/par-list?${queryParams.toString()}`,
        {
          headers: {
            "custom-authorization":
              getSessionStorageItem(STORAGE_KEYS.UserContextToken) || "",
          },
        },
      );
      console.log("Fetched PAR values:", response.data);
      return response.data;
    } catch (error: unknown) {
      console.error("Error fetching PAR values:", error);
      // Preserve Axios error shape so callers can read response.data.message
      throw error;
    }
  }

  // static async getPARValues(
  //   practiceId: number,
  //   siteId?: number,
  // ): Promise<IPARValuesResponse> {
  //   try {
  //     let url = `/onco-forecast/par-values?practice_id=${String(practiceId)}`;
  //     if (siteId) {
  //       url += `&site_id=${siteId}`;
  //     }

  //     const response = await apiClient.get<IPARValuesResponse>(url);
  //     return response.data;
  //   } catch (error: unknown) {
  //     console.error("Error fetching PAR values:", error);
  //     // Preserve Axios error shape so callers can read response.data.message
  //     throw error;
  //   }
  // }
  
  /**
   * Create or update PAR settings
   */
  static async createOrUpdatePARSettings(
    request: CreateParRequest,
  ): Promise<{ status?: string; message?: string; data?: unknown }> {
    try {
      const response = await apiClient.post<{
        status?: string;
        message?: string;
        data?: unknown;
      }>("/customers/par-create-update", request, {
        headers: {
          "custom-authorization":
            getSessionStorageItem(STORAGE_KEYS.UserContextToken) || "",
        },
      });
      return response.data;
    } catch (error: unknown) {
      console.error("Error creating/updating PAR setting:", error);
      throw error;
    }
  }

  /**
   * Delete PAR setting by par id
   */
  static async deletePARSetting(
    parId: number,
    corpEntityId: number,
    practiceId: number,
  ): Promise<{ status?: string; message?: string; data?: unknown }> {
    try {
      const queryParams = new URLSearchParams({
        par_id: String(parId),
        corp_entity_id: String(corpEntityId),
        practice_id: String(practiceId),
      });

      const response = await apiClient.delete<{
        status?: string;
        message?: string;
        data?: unknown;
      }>(`/customers/par-delete/${parId}?${queryParams.toString()}`, {
        headers: {
          "custom-authorization":
            getSessionStorageItem(STORAGE_KEYS.UserContextToken) || "",
        },
      });
      return response.data;
    } catch (error: unknown) {
      console.error("Error deleting PAR setting:", error);
      throw error;
    }
  }
}

export default CustomerConfigService;
